import { Badge } from "@/components/ui/badge";
import { DeathState } from "@/domain/deathData/deathData";
import { VoucherType } from "@/domain/invoices/invoice";
import { BillingFrequency } from "@/domain/offerings/offering";
import { PaymentMethod, PaymentStatus } from "@/domain/payments/payments";
import { BillingPeriodStatus } from "@/domain/subscriptions/subscriptions";
import { convertToDigitStr } from "@/utils/helpers";
import { capitalizeString } from "@/utils/strings";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Clipboard, ClipboardCheck, ClipboardClock, ClipboardMinus, DollarSign } from "lucide-react";

export const formatPaymentMethodBadge = (method: PaymentMethod) => {
  switch (method) {
    case PaymentMethod.CASH:
      return <Badge variant={'outline'} className='text-[10px]'>Efectivo</Badge>
    case PaymentMethod.CREDIT_CARD:
      return <Badge variant={'outline'} className='text-[10px]'>Tja Crédito</Badge>
    case PaymentMethod.DEBIT_CARD:
      return <Badge variant={'outline'} className='text-[10px]'>Tja Débito</Badge>
    case PaymentMethod.BANK_TRANSFER:
      return <Badge variant={'outline'} className='text-[10px]'>Transferencia</Badge>
    case PaymentMethod.OTHER:
      return <Badge variant={'outline'} className='text-[10px]'>Otro</Badge>
    default:
      return method
  }
}

export const RenderPaymentStatus = ({ paymentStatus, collectorCode }: { paymentStatus: PaymentStatus, collectorCode?: number }) => {
  switch (paymentStatus) {
    case PaymentStatus.IN_COLLECTOR:
      return <Badge variant={"primary"} className="w-[max-content]" style={{ fontSize: "10px" }}>Cobrador {collectorCode}</Badge>
    case PaymentStatus.PENDING:
      return <Badge variant="warning" style={{ fontSize: "10px" }}>Pendiente</Badge>
    case PaymentStatus.COMPLETED:
      return <Badge variant="success" style={{ fontSize: "10px" }}>Pagado</Badge>
    case PaymentStatus.FAILED:
      return <Badge variant="destructive" style={{ fontSize: "10px" }}>Error</Badge>
    case PaymentStatus.REFUNDED:
      return <Badge variant="warningLight" style={{ fontSize: "10px" }}>Reembolsado</Badge>
    case PaymentStatus.CANCELLED:
      return <Badge variant="secondary" style={{ fontSize: "10px" }}>Cancelado</Badge>
    default:
      return <Badge variant="destructive" style={{ fontSize: "10px" }}>Error</Badge>
  }
}

export const RenderSubscriptionStatus = ({ paymentStatus, subscriptionStatus, amount, collectorCode }
  : { paymentStatus?: PaymentStatus, subscriptionStatus?: BillingPeriodStatus, amount?: number, collectorCode?: number }) => {
  if (paymentStatus && subscriptionStatus
    && paymentStatus === PaymentStatus.CANCELLED && subscriptionStatus === BillingPeriodStatus.CANCELLED && (amount ?? 0) < 0) {
    return <Badge variant="secondary" style={{ fontSize: "10px" }}>Ajuste</Badge>
  }
  else if (paymentStatus && subscriptionStatus
    && paymentStatus === PaymentStatus.CANCELLED && subscriptionStatus === BillingPeriodStatus.CANCELLED && (amount ?? 0) > 0) {
    return <Badge variant="outline" style={{ fontSize: "10px" }}>Cancelado</Badge>
  }
  else if (subscriptionStatus && subscriptionStatus === BillingPeriodStatus.CANCELLED) {
    return <Badge variant="outline" style={{ fontSize: "10px" }}>Cancelado</Badge>
  }
  else if (paymentStatus && subscriptionStatus
    && paymentStatus === PaymentStatus.COMPLETED && subscriptionStatus === BillingPeriodStatus.PARTIAL) {
    return <Badge variant="light" style={{ fontSize: "10px" }}>Parcial</Badge>
  }
  else if (paymentStatus) switch (paymentStatus) {
    case PaymentStatus.IN_COLLECTOR:
      return <Badge variant={"primary"} className="w-[max-content]" style={{ fontSize: "10px" }}>Cobrador {collectorCode}</Badge>
    case PaymentStatus.PENDING:
      return <Badge variant={"warning"} style={{ fontSize: "10px" }}>Pendiente</Badge>
    case PaymentStatus.COMPLETED:
      return <Badge variant={"success"} style={{ fontSize: "10px" }}>Pagado</Badge>
    case PaymentStatus.FAILED:
      return <Badge variant="destructive" style={{ fontSize: "10px" }}>Error</Badge>
    case PaymentStatus.REFUNDED:
      return <Badge variant="warningLight" style={{ fontSize: "10px" }}>Reembolsado</Badge>
    case PaymentStatus.CANCELLED:
      return <Badge variant="secondary" style={{ fontSize: "10px" }}>Cancelado</Badge>
    default:
      return <Badge variant="destructive" style={{ fontSize: "10px" }}>Error</Badge>
  }
  else if (subscriptionStatus) switch (subscriptionStatus) {
    case BillingPeriodStatus.PENDING:
      return <Badge variant="warning" style={{ fontSize: "10px" }}>Pendiente</Badge>
    case BillingPeriodStatus.PAID:
      return <Badge variant="success" style={{ fontSize: "10px" }}>Pagado</Badge>
    case BillingPeriodStatus.OVERDUE:
      return <Badge variant="destructive" style={{ fontSize: "10px" }}>Adeudado</Badge>
    // case BillingPeriodStatus.CANCELLED:
    //   return <Badge variant="secondary" style={{ fontSize: "10px" }}>Cancelado</Badge>
    case BillingPeriodStatus.PARTIAL:
      return <Badge variant="light" style={{ fontSize: "10px" }}>Parcial</Badge>
    default:
      return <Badge variant="destructive" style={{ fontSize: "10px" }}>Error</Badge>
  }
  return <Badge variant="destructive" style={{ fontSize: "10px" }}>Error</Badge>
}

export const renderDeathEventType = (state: string) => {
  switch (state) {
    case DeathState.CREMATED:
      return "Cremación"
    case DeathState.BURIED:
      return "Inhumación"
    case DeathState.REDUCED:
      return "Reducción"
    case DeathState.EXHUMED:
      return "Exhumación"
    case "MANUAL_EDIT":
      return "Edición Manual"
    default:
      return "Sin Estado"
  }
}

export const OptionalLabel = () =>
  <span className="text-muted-foreground text-sm">{" "} (opcional)</span>

export const PriceLabel = ({ price }: { price: number }) =>
  <div className="flex items-center gap-2">
    <DollarSign className="h-4 w-4 text-muted-foreground" />
    <span className='font-mono'>{convertToDigitStr(price.toString())}</span>
  </div>

export const PaymentLabel = ({ amount, interest }: { amount: number, interest?: boolean }) =>
  <div className="flex items-center gap-2">
    <DollarSign className={`h-4 w-4 ${interest ? "text-warning" : "text-muted-foreground"}`} />
    <span className={`font-mono ${interest ? "text-warning" : ""}`}>{convertToDigitStr(amount.toString())}</span>
  </div>

export const DebtLabel = ({ amount }: { amount: number }) =>
  <div className="flex items-center gap-2">
    <DollarSign className={`h-4 w-4 text-red-500`} />
    <span className={`font-mono text-red-500`}>{convertToDigitStr(amount.toString())}</span>
  </div>

export const RefundLabel = ({ amount }: { amount: number }) =>
  <div className="flex items-center gap-2">
    <DollarSign className={`h-4 w-4 text-gray-400`} />
    <span className={`font-mono text-gray-400`}>{convertToDigitStr(amount.toString())}</span>
  </div>

export const PriceLabelWithTax = ({ price, taxRate }: { price: number, taxRate: number }) =>
  <div className="flex items-center gap-2">
    <DollarSign className="h-4 w-4 text-muted-foreground" />
    <span className='font-mono'>{convertToDigitStr(price.toString())}</span>
    <span className="font-medium text-sm text-gray-500">{` (IVA ${taxRate}%)`}</span>
  </div>

export const PriceLabelWithTaxResponsive = ({ price, taxRate }: { price: number, taxRate: number }) =>
  <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
    <div className="flex items-center gap-1">
      <DollarSign className="h-4 w-4 text-muted-foreground" />
      <span className='font-mono'>{convertToDigitStr(price.toString())}</span>
    </div>
    <span className="font-medium text-sm text-gray-500">{` (IVA ${taxRate}%)`}</span>
  </div>

export const PriceLabelSimple = ({ price, currency, taxRate }: { price: number, currency?: string, taxRate?: number }) =>
  <span className="flex items-center gap-1">
    <span className='price-format font-mono'>{price?.toLocaleString('es-AR')}</span>
    {taxRate !== undefined && <span className="font-medium text-sm text-gray-500">{` (${taxRate}%)`}</span>}
  </span>

export const AmountLabel = ({ amount }: { amount: number }) =>
  <span className="flex items-center gap-1 font-mono">
    {amount !== 0 ? new Intl.NumberFormat('es-AR', {
      signDisplay: 'always'
    }).format(amount) : '-'}
  </span>

export const InlinePriceLabel = ({ price }: { price: number }) =>
  <span className='font-mono'>${price?.toLocaleString('es-AR')}</span>

export const renderPeriod = (periodStartDate: Date, periodEndDate: Date) => (
  <p className="flex items-center gap-2">
    <span className="text-md font-semibold capitalize">{format(periodStartDate, 'MMMM', { locale: es })}/{format(new Date(periodStartDate), 'yyyy', { locale: es })}</span>
    <span className="text-sm text-muted-foreground italic">
      ({format(new Date(periodStartDate), 'dd/MM/yyyy', { locale: es })}
      {" "}-{" "}
      {format(new Date(periodEndDate), 'dd/MM/yyyy', { locale: es })})
    </span>
  </p>
)

export const renderSinglePeriod = (periodStartDate: Date, separator: string = ' ') => (
  capitalizeString(format(new Date(periodStartDate), 'MMMM', { locale: es }) + separator + format(new Date(periodStartDate), 'yyyy', { locale: es }))
)

export const formatPeriodLabel = (periodDate: Date, billingFrequency: BillingFrequency = BillingFrequency.MONTHLY) => {
  return billingFrequency === BillingFrequency.ANNUAL
    ? format(periodDate, 'yyyy')
    : capitalizeString(format(periodDate, 'MMMM', { locale: es })) + '/' + format(new Date(periodDate), 'yyyy', { locale: es })
}

export const getSubscriptionPaymentLabel = (paymentPeriodDate: Date, description?: string) => {
  return description ?
    ` ${description} - ${formatPeriodLabel(paymentPeriodDate)}`
    : formatPeriodLabel(paymentPeriodDate)
}

export const getSubscriptionLabel = (periodStartDate: Date, periodEndDate: Date, billingFrequency: BillingFrequency = BillingFrequency.MONTHLY) => {
  const startMonth = periodStartDate.getMonth()
  const startYear = periodStartDate.getFullYear()
  const endMonth = periodEndDate.getMonth()
  const endYear = periodEndDate.getFullYear()

  const spansMultipleCycles = billingFrequency === BillingFrequency.MONTHLY
    ? startMonth !== endMonth || startYear !== endYear
    : startYear !== endYear

  if (!spansMultipleCycles) {
    return formatPeriodLabel(periodStartDate, billingFrequency)
  }

  return `${formatPeriodLabel(periodStartDate, billingFrequency)} - ${formatPeriodLabel(periodEndDate, billingFrequency)}`
}

export const renderDatetime = (_date: Date | string | null) => {
  if (!_date) return '-'
  const date = _date instanceof Date ? _date : new Date(_date)
  const isZeroHour = date.getHours() === 0 && date.getMinutes() === 0
  return <div className="flex flex-col gap-1 text-sm">
    <span>{format(_date, "dd/MM/yyyy")}</span>
    <span className="text-xs font-medium">{isZeroHour ? "" : format(_date, "HH:mm") + 'hs'}</span>
  </div>
}

export const renderDatetimeInline = (_date: Date | string | null) => {
  if (!_date) return '-'
  const date = _date instanceof Date ? _date : new Date(_date)
  const isZeroHour = date.getHours() === 0 && date.getMinutes() === 0
  return <div className="flex gap-2 items-center text-sm">
    <span>{format(_date, "dd/MM/yyyy")}</span>
    <span className="text-xs font-medium">{isZeroHour ? "" : format(_date, "HH:mm") + 'hs'}</span>
  </div>
}

export const renderDate = (_date: Date | string | null) => {
  if (!_date) return '-'
  const date = _date instanceof Date ? _date : new Date(_date)
  return <div className="flex flex-col gap-1 text-sm">
    <span>{format(_date, "dd/MM/yyyy")}</span>
  </div>
}

export const downloadCsv = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const formatNumEsAr = (n: number, decimals: number = 2) =>
  n.toLocaleString('es-AR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(amount)
}

export const getVoucherIcon = (voucherType: VoucherType, invoiceNumber?: string) => {
  if (voucherType === VoucherType.INVOICE && !invoiceNumber) {
    return <ClipboardClock className="h-3.5 w-3.5 text-warning" />
  }
  if (voucherType === VoucherType.INVOICE) {
    return <ClipboardCheck className="h-3.5 w-3.5" />
  }
  if (voucherType === VoucherType.CREDIT_NOTE) {
    return <ClipboardMinus className="h-3.5 w-3.5" />
  }
  return <Clipboard className="h-3.5 w-3.5" />
}
